import React, { useState, useEffect } from 'react';

function PhotoGalleryModal({ isOpen, onClose, shadeType, shadeName }) {
  const [photos, setPhotos] = useState([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState({});

  useEffect(() => {
    if (isOpen && shadeType) {
      // Load gallery data
      setLoading(true);
      fetch('https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/products.json')
        .then(res => res.json())
        .then(data => {
          if (data.photoGalleries && data.photoGalleries[shadeType]) {
            setPhotos(data.photoGalleries[shadeType]);
            setSelectedPhotoIndex(0);
            setImageLoaded({});
          }
          setLoading(false);
        })
        .catch(err => {
          console.error('Error loading gallery:', err);
          setLoading(false);
        });
    }
  }, [isOpen, shadeType]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        navigatePhoto(-1);
      } else if (e.key === 'ArrowRight') {
        navigatePhoto(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedPhotoIndex, photos.length]);

  const navigatePhoto = (direction) => {
    setSelectedPhotoIndex(prev => {
      const newIndex = prev + direction;
      if (newIndex < 0) return photos.length - 1;
      if (newIndex >= photos.length) return 0;
      return newIndex;
    });
  };

  const handleImageLoad = (index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  };

  if (!isOpen) return null;

  const selectedPhoto = photos[selectedPhotoIndex];

  return (
    <div 
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-90"></div>
      
      {/* Modal Content - constrained height */}
      <div 
        className="tw-relative tw-max-w-4xl tw-w-full tw-h-[90vh] tw-bg-white tw-rounded-lg tw-shadow-2xl tw-flex tw-flex-col tw-overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Content - full height */}
        <div className="tw-flex-1 tw-flex tw-flex-col tw-min-h-0">
          {/* Main Image Container - optimized for square images */}
          <div className="tw-flex-1 tw-bg-gray-100 tw-relative tw-flex tw-items-center tw-justify-center tw-min-h-0">
            {/* Floating Close Button - over the image */}
            <button
              onClick={onClose}
              className="tw-absolute tw-top-4 tw-right-4 tw-z-10 tw-bg-white tw-p-2 tw-rounded-full tw-shadow-lg hover:tw-bg-gray-100 tw-transition-colors"
              aria-label="Close gallery"
            >
              <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
              {loading ? (
                <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-brand-red"></div>
              ) : selectedPhoto ? (
                <>
                  {!imageLoaded[selectedPhotoIndex] && (
                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                      <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-brand-red"></div>
                    </div>
                  )}
                  <img
                    src={selectedPhoto.url}
                    alt={selectedPhoto.description}
                    className={`tw-w-auto tw-h-auto tw-object-contain tw-transition-opacity tw-duration-300 ${
                      imageLoaded[selectedPhotoIndex] ? 'tw-opacity-100' : 'tw-opacity-0'
                    }`}
                    style={{ 
                      maxWidth: 'min(500px, 100%)', 
                      maxHeight: 'min(500px, 100%)' 
                    }}
                    loading="lazy"
                    onLoad={() => handleImageLoad(selectedPhotoIndex)}
                  />
                
                {/* Navigation Arrows - overlaying the image */}
                {photos.length > 1 && (
                  <>
                    <button
                      onClick={() => navigatePhoto(-1)}
                      className="tw-absolute tw-left-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-white tw-bg-opacity-90 tw-text-gray-800 tw-p-3 tw-rounded-full tw-shadow-lg hover:tw-bg-opacity-100 tw-transition-all"
                      aria-label="Previous photo"
                    >
                      <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => navigatePhoto(1)}
                      className="tw-absolute tw-right-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-white tw-bg-opacity-90 tw-text-gray-800 tw-p-3 tw-rounded-full tw-shadow-lg hover:tw-bg-opacity-100 tw-transition-all"
                      aria-label="Next photo"
                    >
                      <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                
                {/* Photo Counter */}
                <div className="tw-absolute tw-bottom-4 tw-left-4 tw-bg-white tw-bg-opacity-90 tw-text-gray-800 tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-shadow">
                  {selectedPhotoIndex + 1} / {photos.length}
                </div>
              </>
            ) : (
              <div className="tw-text-gray-500">
                No photos available
              </div>
            )}
            </div>
          </div>

          {/* Description - larger text */}
          {selectedPhoto && (
            <div className="tw-px-6 tw-py-4 tw-bg-gray-50 tw-border-t tw-flex-shrink-0">
              <p className="tw-text-center tw-text-gray-700 tw-text-lg tw-font-medium">{selectedPhoto.description}</p>
            </div>
          )}

          {/* Thumbnail Strip */}
          {photos.length > 1 && (
            <div className="tw-px-4 tw-py-3 tw-border-t tw-bg-white tw-flex-shrink-0">
              <div className="tw-flex tw-gap-2 tw-justify-center tw-overflow-x-auto">
                {photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedPhotoIndex(index)}
                    className={`tw-flex-shrink-0 tw-w-20 tw-h-20 tw-rounded tw-overflow-hidden tw-border-2 tw-transition-all ${
                      selectedPhotoIndex === index
                        ? 'tw-border-brand-red tw-shadow-md'
                        : 'tw-border-gray-300 hover:tw-border-gray-400'
                    }`}
                  >
                    <img
                      src={photo.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="tw-w-full tw-h-full tw-object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PhotoGalleryModal;